"use client";

import { HeartIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { BANKS } from "../banks";
import { CheckIcon } from "../CheckIcon";
import { DidYouKnowDisclosure } from "../DidYouKnowDisclosure";
import { FlowSelect } from "../FlowSelect";
import { FounderTipCard } from "../FounderTipCard";
import { MonthlyExpensesSelect } from "../MonthlyExpensesSelect";
import { SHAREHOLDER_OPTIONS, TIMELINE_OPTIONS } from "../options";
import { TopSheen } from "../TopSheen";
import { NOT_INCLUDED, buildPackageBreakdown, type EditableSelection } from "../pricing";
import type { GetStartedData } from "../GetStartedFlow";

/**
 * Extra copy the design attaches to particular package lines, keyed by the
 * breakdown row so the list itself stays derived from the running total.
 *
 * NOTE: the export repeats "Incorporation Fee to Company Registry" and its
 * "Registration filling and name application" description on the fourth row
 * too — but that row's bullets ("Unlimited company changes", "Designated
 * Representative…") are clearly the Company Secretary's, so they're keyed as
 * such here and the duplicated description is dropped rather than repeated
 * wrongly.
 */
const ROW_DETAILS: Record<
  string,
  { description?: string; bullets?: string[]; bulletStyle?: "dot" | "check" }
> = {
  "incorporation-fee": {
    description: "Registration filling and name application",
    bulletStyle: "dot",
    bullets: [
      "HK Company Registration",
      "PayPal with VIP Support (6% dispute tolerance)",
      "Payoneer VIP Account and 5+ more banks",
      "Airwallex Account",
     
    ],
  },
  secretary: {
    bulletStyle: "check",
    bullets: [
      "Unlimited company changes",
      "Designated Representative at no extra cost",
      "Local experts in Hong Kong regulations",
    ],
  },
};

function DetailField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex w-full flex-col items-start gap-2">
      <label htmlFor={htmlFor} className="font-inter text-sm font-bold text-[#F3F4F6]">
        {label}
      </label>
      {children}
    </div>
  );
}

export function PackageReadyStep({
  data,
  onChange,
  onCheckout,
}: {
  data: GetStartedData;
  /** Patches the flow's state, so every edit here re-prices the package. */
  onChange: (patch: Partial<GetStartedData>) => void;
  onCheckout: () => void;
}) {
  const { rows, dueToday } = buildPackageBreakdown(data);

  // NOT_INCLUDED is a sentinel for the dropdowns; the flow stores null.
  const setSelection = (field: EditableSelection, value: string) =>
    onChange({ [field]: value === NOT_INCLUDED ? null : value } as Partial<GetStartedData>);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onCheckout();
      }}
      // Full-width review: this screen has no summary sidebar, so the running
      // total moves inline beside the checkout button.
      className="mx-auto flex w-full max-w-[1240px] flex-col items-start gap-6 px-4 pb-16 pt-8 sm:gap-8 sm:px-6 sm:pb-24 sm:pt-12 lg:pt-[31px]"
    >
      <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
        <h1 className="font-suisse-intl-bold text-[26px] leading-tight text-white sm:text-4xl md:text-5xl lg:text-[56px] lg:leading-[71px]">
          Your company setup package is ready
        </h1>
        <p className="font-suisse-intl-medium text-base leading-relaxed text-white sm:text-lg sm:leading-[28.8px]">
          Based on what you told us, we suggest moving forward with the following services.{" "}
          <br className="max-sm:hidden" /> Change anything below and the price updates as you go.
        </p>
      </div>

      {/* This page has no summary sidebar, so the total is what sticks. Offsets
          clear the site nav plus StepShell's sticky header, and z-30 keeps it
          under that header (z-40) while staying above the page content. */}
      <div className="sticky top-[134px] z-30 flex w-full flex-wrap items-end justify-between gap-4 rounded-2xl bg-black/60 px-4 py-3 backdrop-blur-md xl:top-[150px]">
        {/* Labelled on phones: pinned on its own, a bare figure with no
            sidebar next to it doesn't say what it is. */}
        <span className="flex flex-col">
          <span className="font-inter text-xs font-medium uppercase tracking-wide text-[#8B949E] sm:hidden">
            Due today
          </span>
          <span className="font-suisse-intl-bold text-[26px] leading-8 text-white sm:text-[32px]">
            {dueToday}
          </span>
        </span>
        {/* <button
          type="submit"
          className="flex h-14 items-center  justify-center rounded-full bg-white px-4 font-suisse-intl text-xl leading-5 text-[#2A4775] transition-opacity hover:opacity-90"
        >
          Continue to checkout
        </button> */}
      </div>

      {/* Top-edge sheen instead of the export's full white outline, matching
          the flow's fields and the "Did you know?" disclosure. The outline has
          to go for it to read: a solid white border sits directly over the
          gradient and hides it. */}
      <div className="relative flex w-full items-start gap-3 rounded-2xl bg-[#0F1319] p-5 sm:w-auto sm:gap-4 sm:p-6">
        <TopSheen />
        <HeartIcon
          aria-hidden
          className="h-9 w-9 shrink-0 fill-[#FF585B] text-[#FF585B] sm:h-12 sm:w-12"
        />
        <div className="flex flex-col items-start justify-center gap-1.5 sm:gap-2">
          <h2 className="font-inter text-lg font-bold leading-snug text-white sm:text-[22px]">
            100% Satisfaction Guaranteed
          </h2>
          <p className="font-inter text-sm text-[#9CA3AF]">
            We will refund the fees engaged within the first 30 days, should you be unhappy with our
            service.
          </p>
        </div>
      </div>

      <h2 className="font-inter text-lg font-bold text-white sm:text-[22px]">
        What&apos;s included
      </h2>

      <hr className="w-full border-t border-[#283143]" />

      {/* Every adjustable category gets a row — including ones currently left
          out — so a plan skipped earlier can still be added back here. */}
      <div className="flex w-full flex-col items-start gap-6">
        {rows.map((row) => {
          const detail = ROW_DETAILS[row.key];
          const notIncluded = row.amount === "Not included";

          return (
            <div key={row.key} className="flex w-full flex-col items-start gap-3 sm:gap-4">
              {/* Label and price share a row at every width, with the dropdown
                  on its own line beneath. The selects aren't in the export —
                  they came with making this page editable — so they're free to
                  sit below rather than being squeezed beside the price. */}
              <div className="flex w-full items-start justify-between gap-3 sm:items-end sm:gap-4">
                <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5 sm:gap-[10px]">
                  <span
                    className={cn(
                      "font-inter text-base font-bold sm:text-lg",
                      row.credit ? "italic text-[#39D353]" : "text-[#F3F4F6]",
                    )}
                  >
                    {row.label}
                  </span>
                  {detail?.description && (
                    <span className="font-inter text-sm text-[#9CA3AF] sm:text-base">
                      {detail.description}
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-baseline gap-1.5 sm:gap-2">
                  <span
                    className={cn(
                      "font-suisse-intl-bold leading-[22px]",
                      notIncluded
                        ? "text-sm text-white/50 sm:text-lg"
                        : "text-xl sm:text-[32px]",
                      row.credit ? "text-[#39D353]" : notIncluded ? "" : "text-white",
                    )}
                  >
                    {row.amount}
                  </span>
                  {row.period && (
                    <span className="font-suisse-intl text-xs leading-[22px] text-white/70 sm:text-base">
                      {row.period}
                    </span>
                  )}
                </div>
              </div>

              {row.edit && (
                <FlowSelect
                  ariaLabel={row.label}
                  value={row.edit.value}
                  onChange={(value) => setSelection(row.edit!.field, value)}
                  options={row.edit.options}
                  className="sm:max-w-[420px]"
                />
              )}

              {detail?.bullets && (
                <ul
                  className={cn(
                    "flex w-full flex-col items-start",
                    detail.bulletStyle === "check" ? "gap-2" : "gap-3",
                  )}
                >
                  {detail.bullets.map((bullet) => (
                    // items-start: these wrap on a phone, and centring parks
                    // the marker halfway down a two-line item.
                    <li key={bullet} className="flex w-full items-start gap-[10px]">
                      {detail.bulletStyle === "check" ? (
                        <CheckIcon className="relative mt-0.5 block h-6 w-6 shrink-0" />
                      ) : (
                        <span
                          aria-hidden
                          className="mt-1.5 h-[10px] w-[10px] shrink-0 rounded-full bg-[#D9D9D9]"
                        />
                      )}
                      <span
                        className={cn(
                          "flex-1 font-inter",
                          detail.bulletStyle === "check"
                            ? "text-sm font-bold leading-[19.6px] text-[#F3F4F6]"
                            : "text-[13px] leading-[18.2px] text-[#CCCCCC]",
                        )}
                      >
                        {bullet}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <hr className="w-full border-t border-[#283143]" />

      {/* The non-priced answers from earlier steps, editable in place. */}
      <h2 className="font-inter text-lg font-bold text-white sm:text-[22px]">Your details</h2>

      <div className="grid w-full gap-5 sm:grid-cols-2 sm:gap-6">
        <DetailField label="Company name" htmlFor="review-company-name">
          <div className="relative w-full">
            <input
              id="review-company-name"
              type="text"
              value={data.companyName}
              onChange={(e) => onChange({ companyName: e.target.value, companyNameSkipped: false })}
              placeholder="Not decided yet"
              className="h-14 w-full rounded-lg bg-[#0F1319] px-6 font-suisse-intl-medium text-base text-white placeholder:text-white/50 focus:outline-none"
            />
          </div>
        </DetailField>

        <DetailField label="When do you plan to register?">
          <FlowSelect
            ariaLabel="When do you plan to register?"
            value={data.registerTimeline ?? ""}
            onChange={(registerTimeline) => onChange({ registerTimeline })}
            options={TIMELINE_OPTIONS}
            placeholder="Not answered"
          />
        </DetailField>

        <DetailField label="Shareholders">
          <FlowSelect
            ariaLabel="How many shareholders do you have?"
            value={data.shareholders}
            onChange={(shareholders) => onChange({ shareholders })}
            options={SHAREHOLDER_OPTIONS.map((option) => ({ value: option, label: option }))}
          />
        </DetailField>

        <DetailField label="Banking partner">
          <FlowSelect
            ariaLabel="Banking partner"
            value={data.bankingPartner ?? NOT_INCLUDED}
            onChange={(value) =>
              onChange({ bankingPartner: value === NOT_INCLUDED ? null : value })
            }
            options={[
              ...BANKS.map((bank) => ({ value: bank.id, label: `${bank.name} — ${bank.perk}` })),
              { value: NOT_INCLUDED, label: "Set up later" },
            ]}
          />
        </DetailField>
      </div>

      {/* Monthly expenses drives which accounting tier is recommended, so it
          stays adjustable alongside the plan itself. */}
      <div className="flex w-full flex-col items-start gap-2">
        <MonthlyExpensesSelect
          value={data.monthlyExpenses}
          onChange={(monthlyExpenses) => onChange({ monthlyExpenses })}
        />
        <DidYouKnowDisclosure />
      </div>

      <FounderTipCard>
        The IRD only recognises expenses that are properly recorded. Clean books from day one mean
        you only pay tax on what you actually owe — nothing more.
      </FounderTipCard>

      <div className="flex w-full items-center justify-end gap-4 pt-4 sm:pt-6">
        <button
          type="submit"
          className="flex h-14 w-full items-center justify-center rounded-full bg-white px-12 font-suisse-intl text-xl leading-5 text-[#2A4775] transition-opacity hover:opacity-90 sm:w-auto"
        >
          Next
        </button>
      </div>
    </form>
  );
}
